import { loadVimeoAPI } from "@/scripts/utils";

class BackgroundVideo extends HTMLElement {
  constructor() {
    super()

    this.video = this.querySelector('video')
    this.pauseButton = this.querySelector('background-video-pause-button')
    this.initSources = this.dataset.initSources || 'true'
    this.audioEl = this.querySelector('.js-audio-control')

    this.videoMobile = this.dataset.videoMobile
    this.videoDesktop = this.dataset.videoDesktop
    this.delay = this.dataset.delay === 'true' || false
    this.videoSrc = ''
    this.videoPause = this.dataset.videoPause === 'true' || false
    this.setVideoSrc()

    this.isVimeo = this.dataset.isVimeo === 'true';

    if (this.isVimeo) {
      this.setupVimeo();
      return;
    }

    if (this.delay && window.innerWidth < 640) {
      let videoLoaded = false
      const userInteractionEvents = ["scroll", "mousedown", "mousemove", "click", "keydown", "touchstart"]
      userInteractionEvents.forEach(event => {
        window.addEventListener(event, () => {
          if (!videoLoaded) {
            videoLoaded = true
            this.setupVideo()
          }
        }, { passive: true })
      })

    } else {
      this.setupVideo()
    }


    this.video.addEventListener('canplay', () => {
      this.video.classList.remove('opacity-0')
      window.addEventListener('resize', this.updateSrc.bind(this))
    })

    if (this.initSources == 'true') {
      this.updateSrc()
    }
  }

  setupVimeo() {
    const vimeoWrapper = this.querySelector('.vimeo-wrapper');
    const vimeoId = vimeoWrapper.dataset.vimeoId;
    const autoplay = vimeoWrapper.dataset.autoplay === 'true';
    const muted = vimeoWrapper.dataset.muted === 'true';
    const loop = vimeoWrapper.dataset.loop === 'true';
    const mobileSrc = vimeoWrapper.dataset.videoMobile;
    const desktopSrc = vimeoWrapper.dataset.videoDesktop;
    const currentId = window.innerWidth < 640 ? mobileSrc : desktopSrc;
    const finalId = currentId.split('playback/').pop().split('/')[0];

    loadVimeoAPI().then(() => {
      const player = new window.Vimeo.Player(vimeoWrapper, {
        id: finalId,
        autoplay,
        muted,
        loop,
        controls: false,
        background: true,
        responsive: true,
      });

      this.vimeoPlayer = player;

      player.on('play', () => {
        this.pauseButton?.setAttribute('data-playing', 'true')
      })

      player.on('pause', () => {
        this.pauseButton?.setAttribute('data-playing', 'false')
      })

      player.on('loaded', () => {
        vimeoWrapper.classList.remove('opacity-0')

        if (loop) {
          player.setLoop(true)
        }

        // Force update initial state
        player.getPaused().then(paused => {
          this.pauseButton?.setAttribute('data-playing', paused ? 'false' : 'true')
        })

        if (!this.videoPause && autoplay) {
          player.play()
        }
      })

      this.pauseButton?.addEventListener('click', () => {
        player.getPaused().then((paused) => {
          if (paused) {
            player.play();
            this.pauseButton.setAttribute('data-playing', 'true');
          } else {
            player.pause();
            this.pauseButton.setAttribute('data-playing', 'false');
          }
        });
      });
    });
  }

  toggleAudio(el) {
    this.video.muted = false
    if (el.classList.contains('active')) {
      this.video.volume = 0
    } else {
      this.video.volume = 1
    }

    el.classList.toggle('active')
  }

  pause() {
    this.video.pause()
    this.pauseButton.setAttribute('data-playing', 'false')
    this.pauseButton.setAttribute('aria-label', this.pauseButton.getAttribute('data-play'))
  }


  resume() {
    if (this.video && this.video.src) {
      this.video.play().catch((err) => {
        this.pauseButton?.setAttribute('data-playing', 'false')
      })
    } else if (this.vimeoPlayer) {
      this.vimeoPlayer.play().catch((err) => {
        this.pauseButton?.setAttribute('data-playing', 'false')
      })
    }
  }

  setupVideo() {
    this.video.src = this.videoSrc
    this.video.load()

    const fallbackTimeout = setTimeout(() => {
      if (this.video.paused || this.video.readyState < 3) {
        this.pauseButton?.setAttribute('data-playing', 'false')
      }
    }, 2000)

    this.video.addEventListener('canplay', () => {
      clearTimeout(fallbackTimeout)
      if (!this.videoPause) {
        this.video.play().catch((err) => {
          this.pauseButton?.setAttribute('data-playing', 'false')
        })
      } else {
        this.video.pause()
        this.pauseButton?.setAttribute('data-playing', 'false')
      }
    })

    this.video.addEventListener('error', (e) => {
      clearTimeout(fallbackTimeout)
      this.pauseButton?.setAttribute('data-playing', 'false')
    })

    this.video.addEventListener('playing', () => {
      this.pauseButton?.setAttribute('data-playing', 'true')
    })
  }

  setVideoSrc() {
    this.videoSrc = window.innerWidth < 640 ? this.videoMobile : this.videoDesktop
  }


  updateSrc() {
    const sources = this.querySelectorAll('video source')
    let selectedSource

    sources.forEach((sourceEl) => {
      const sourceMediaQuery = sourceEl.getAttribute('media')

      if (!sourceMediaQuery) {
        selectedSource = sourceEl
      } else if (window.matchMedia(sourceMediaQuery).matches) {
        selectedSource = sourceEl
      }
    })

    const selectedSourceValue = selectedSource.getAttribute('data-src')

    this.pauseButton?.setAttribute('data-playing', 'true')

    if (this.video.src !== selectedSourceValue) {
      this.video.src = selectedSourceValue
    }
  }
}

if (!customElements.get('background-video')) {
  customElements.define('background-video', BackgroundVideo)
}

class BackgroundVideoPauseButton extends HTMLElement {
  constructor() {
    super()

    this.bgVideo = this.closest('background-video')
  }

  handleClick() {
    if (!this.bgVideo.video.paused) {
      this.bgVideo.pause()
    } else {
      this.bgVideo.resume()
    }
  }

  handleClickBtn() {
    this.handleClick()
  }

  handleKeyDownBtn(event) {
    if (event.key === " " || event.key === "Enter" || event.key === "Spacebar") {
      this.handleClick()
    }
  }
}

if (!customElements.get('background-video-pause-button')) {
  customElements.define('background-video-pause-button', BackgroundVideoPauseButton)
}
